<!-- src/pages/HomeView.vue -->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery } from '@vue/apollo-composable'
import { storeToRefs } from 'pinia'
import { GET_MEALS_BY_DATE_RANGE, GET_SHOPPING_LISTS, GET_RECIPES, GET_TOP_COOKED_RECIPES, GET_TOP_BOUGHT_ITEMS } from '../graphql/queries'
import type { TopCookedRecipe, TopBoughtItem } from '../graphql/types'
import { useAuthStore } from '../stores/auth'
import { useGroupsStore } from '../stores/groups'
import { formatDate } from '../utils/dateHelpers'
import dayjs from 'dayjs'

const router = useRouter()
const authStore = useAuthStore()

const currentUser = computed(() => authStore.currentUser)
const currentGroup = computed(() => authStore.activeGroup)

const groupsStore = useGroupsStore()
const { members } = storeToRefs(groupsStore)
const memberCount = computed(() => members.value.length || '—')

const greeting = computed(() => {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
})

const todayLabel = computed(() =>
  dayjs().format('dddd, MMMM D')
)

// Current week range
const getCurrentWeekRange = () => {
  const now = dayjs()
  const start = now.startOf('week').add(1, 'day')
  return { start: start.format('DD/MM/YYYY'), end: start.add(6, 'day').format('DD/MM/YYYY') }
}
const currentWeek = getCurrentWeekRange()

// Queries
const { result: weekMealsResult, loading: loadingMeals } = useQuery(GET_MEALS_BY_DATE_RANGE, {
  start_date: currentWeek.start,
  end_date: currentWeek.end,
  show_active: false,
})
const { result: shoppingListsResult } = useQuery(GET_SHOPPING_LISTS)
const { result: recipesResult } = useQuery(GET_RECIPES, { limit: 100, page: 0, search: '' })

// Computed
const upcomingMeals = computed(() => {
  const today = dayjs().startOf('day')
  return [...(weekMealsResult.value?.mealsByDateRange || [])]
    .filter(m => dayjs(m.scheduled_date).isSame(today) || dayjs(m.scheduled_date).isAfter(today))
    .sort((a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime())
})

const todayMeals = computed(() =>
  upcomingMeals.value.filter(m => dayjs(m.scheduled_date).isSame(dayjs(), 'day'))
)

const allShoppingLists = computed(() => shoppingListsResult.value?.shoppingLists || [])
const activeList = computed(() => allShoppingLists.value.find((l: any) => l.status === 'active'))
const totalRecipes = computed(() => recipesResult.value?.recipes?.length || 0)

const activeListProgress = computed(() => {
  if (!activeList.value) return 0
  const list = activeList.value
  const total = list.items_count ?? list.items?.length ?? 0
  const bought = list.bought_items_count ?? list.items?.filter((i: any) => i.is_purchased)?.length ?? 0
  if (total === 0) return 0
  return Math.round((bought / total) * 100)
})

// Week planner days
const weekDays = computed(() => {
  const today = dayjs()
  const monday = today.startOf('week').add(1, 'day')
  return Array.from({ length: 7 }, (_, i) => {
    const d = monday.add(i, 'day')
    const mealsOnDay = (weekMealsResult.value?.mealsByDateRange || []).filter(
      (m: any) => dayjs(m.scheduled_date).isSame(d, 'day')
    )
    return {
      _date: d,
      day: d.format('ddd'),
      date: d.format('D'),
      isToday: d.isSame(today, 'day'),
      meals: mealsOnDay.length,
    }
  })
})

const mealTypeBg = (typeName: string) =>
  typeName?.toLowerCase().includes('cena') || typeName?.toLowerCase().includes('dinner')
    ? 'rgba(255,152,0,.1)' : '#EDF3EC'

const mealTypeColor = (typeName: string) =>
  typeName?.toLowerCase().includes('cena') || typeName?.toLowerCase().includes('dinner')
    ? '#FF9800' : '#4F8646'

const getInitials = (title: string) =>
  title.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('')

const goToMealsDay = (date: dayjs.Dayjs) => {
  const d = date.format('YYYY-MM-DD')
  router.push(`/meals?from=${d}&to=${d}`)
}

// Kitchen Insights
const statsDays = ref(30)
const statsPeriodOptions = [
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
  { label: '1 year', value: 365 },
]

const { result: topRecipesResult, loading: loadingRecipes } = useQuery<{ topCookedRecipes: TopCookedRecipe[] }>(
  GET_TOP_COOKED_RECIPES,
  computed(() => ({ days: statsDays.value, limit: 8 })),
  { fetchPolicy: 'no-cache' },
)
const { result: topItemsResult, loading: loadingItems } = useQuery<{ topBoughtItems: TopBoughtItem[] }>(
  GET_TOP_BOUGHT_ITEMS,
  computed(() => ({ days: statsDays.value, limit: 8 })),
  { fetchPolicy: 'no-cache' },
)

const topRecipes = computed(() => topRecipesResult.value?.topCookedRecipes || [])
const topItems = computed(() => topItemsResult.value?.topBoughtItems || [])

const maxRecipeCount = computed(() => Math.max(...topRecipes.value.map((r) => r.count), 1))
const maxItemCount = computed(() => Math.max(...topItems.value.map((i) => i.count), 1))

const statsPeriodLabel = computed(() => {
  if (statsDays.value === 30) return 'Last 30 days'
  if (statsDays.value === 90) return 'Last 90 days'
  return 'Last year'
})

onMounted(() => {
  const groupId = (currentGroup.value?.active_group as any)?.id
  if (groupId) groupsStore.fetchMembers(groupId)
})
</script>

<template>
  <div class="page-enter home-page">

    <!-- ── Hero Banner ──────────────────────────────────────── -->
    <div class="hero-padding hero-banner">
      <div class="hero-texture" />
      <div class="hero-inner">
        <p class="hero-date-label">
          {{ todayLabel }} · {{ currentGroup?.active_group?.name || 'Personal' }}
        </p>
        <h1 class="font-display hero-title">
          {{ greeting }}, {{ currentUser?.first_name || 'there' }}.
        </h1>
        <p class="hero-subtitle">
          You have <strong class="hero-strong">{{ upcomingMeals.length }} meal{{ upcomingMeals.length !== 1 ? 's' : ''
            }}</strong> planned this week
          <template v-if="activeList"> and <strong class="hero-strong">1 active shopping list.</strong></template>
        </p>
        <div class="hero-actions">
          <button data-tour="qa-plan" @click="router.push('/meals')" class="hero-btn">+ Plan a Meal</button>
          <button data-tour="qa-recipes" @click="router.push('/recipes')" class="hero-btn">Browse Recipes</button>
          <button data-tour="qa-shopping" @click="router.push('/shopping-lists')" class="hero-btn">Shopping List</button>
        </div>
      </div>
    </div>

    <!-- ── Page Body ────────────────────────────────────────── -->
    <div class="page-body-container">

      <!-- Stats grid -->
      <div class="grid-responsive-stats">
        <div class="ds-card stat-card">
          <div class="stat-icon stat-icon--green">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path
                d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"
                fill="#4F8646" />
            </svg>
          </div>
          <div class="font-display stat-number">{{ totalRecipes }}</div>
          <div class="stat-label">Recipes</div>
          <div class="stat-sublabel">in your kitchen</div>
        </div>
        <div class="ds-card stat-card">
          <div class="stat-icon stat-icon--green">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path
                d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"
                fill="#4F8646" />
            </svg>
          </div>
          <div class="font-display stat-number">{{ upcomingMeals.length }}</div>
          <div class="stat-label">Meals Planned</div>
          <div class="stat-sublabel">this week</div>
        </div>
        <div class="ds-card stat-card">
          <div class="stat-icon stat-icon--orange">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path
                d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"
                fill="#FF9800" />
            </svg>
          </div>
          <div class="font-display stat-number">{{ allShoppingLists.length }}</div>
          <div class="stat-label">Shopping Lists</div>
          <div class="stat-sublabel">{{ activeList ? '1 active' : 'none active' }}</div>
        </div>
        <div class="ds-card stat-card">
          <div class="stat-icon stat-icon--gray">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path
                d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
                fill="#64748B" />
            </svg>
          </div>
          <div class="font-display stat-number">{{ memberCount }}</div>
          <div class="stat-label">Group Members</div>
          <div class="stat-sublabel">{{ currentGroup?.active_group?.name || 'Personal' }}</div>
        </div>
      </div>

      <!-- Week planner strip -->
      <div class="week-section">
        <div class="section-header-row">
          <h2 class="font-display section-title">This Week</h2>
          <button @click="router.push('/meals')" class="link-btn">Full calendar →</button>
        </div>
        <div class="week-strip">
          <div v-for="(d, i) in weekDays" :key="i" class="week-day" :class="{ today: d.isToday }"
            @click="goToMealsDay(d._date)">
            <div class="day-label" :class="{ today: d.isToday }">{{ d.day }}</div>
            <div class="font-display day-date" :class="{ today: d.isToday }">{{ d.date }}</div>
            <template v-if="d.meals > 0">
              <div class="meal-dots">
                <div v-for="j in Math.min(d.meals, 2)" :key="j" class="meal-dot"
                  :style="{ opacity: j === 2 ? 0.5 : 1 }" />
              </div>
              <div class="day-meal-count">{{ d.meals }} meal{{ d.meals > 1 ? 's' : '' }}</div>
            </template>
          </div>
        </div>
      </div>

      <!-- Two-column main content -->
      <div class="grid-responsive-main">

        <!-- Left: Upcoming Meals -->
        <div>
          <div class="section-header-row section-header-row--lg">
            <h2 class="font-display section-title">Upcoming Meals</h2>
            <button @click="router.push('/meals')" class="link-btn">View all →</button>
          </div>

          <!-- Loading -->
          <div v-if="loadingMeals" class="meals-loading">
            <div v-for="n in 3" :key="n" class="ds-card skeleton meals-skeleton" />
          </div>

          <!-- Empty state -->
          <div v-else-if="upcomingMeals.length === 0" class="ds-card meals-empty">
            <div class="meals-empty-icon">🍽️</div>
            <div class="meals-empty-title">No meals planned yet</div>
            <div class="meals-empty-desc">Start planning your week by adding meals.</div>
            <button @click="router.push('/meals')" class="ds-btn-primary">+ Plan a Meal</button>
          </div>

          <!-- Meal list -->
          <div v-else class="meals-list">
            <div v-for="meal in upcomingMeals.slice(0, 5)" :key="meal.id" class="ds-card meal-item"
              @click="router.push('/meals')">
              <div class="meal-icon">
                <span class="meal-initials">{{ getInitials(meal.title) }}</span>
              </div>
              <div class="meal-info">
                <div class="meal-title">{{ meal.title }}</div>
                <div class="meal-date">{{ formatDate(meal.scheduled_date) }}</div>
              </div>
              <span v-if="meal.type?.name" class="ds-badge"
                :style="{ background: mealTypeBg(meal.type.name), color: mealTypeColor(meal.type.name) }">
                {{ meal.type.name }}
              </span>
              <svg class="meal-chevron" width="14" height="14" fill="none" viewBox="0 0 24 24">
                <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <div v-if="upcomingMeals.length > 5" class="meals-more">
              <button @click="router.push('/meals')" class="link-btn">
                + {{ upcomingMeals.length - 5 }} more meals this week
              </button>
            </div>
          </div>

          <!-- Today's menu highlight -->
          <div v-if="todayMeals.length > 0" class="today-menu">
            <div class="today-menu-title">On the menu today</div>
            <div class="today-menu-list">
              <div v-for="meal in todayMeals" :key="meal.id" class="today-menu-item">
                <div class="today-menu-dot" />
                <span class="today-menu-name">{{ meal.title }}</span>
                <span v-if="meal.type?.name" class="ds-badge today-menu-badge"
                  :style="{ background: mealTypeBg(meal.type.name), color: mealTypeColor(meal.type.name) }">
                  {{ meal.type.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- Kitchen Insights -->
          <div class="insights-section" data-tour="insights">
            <div class="insights-header">
              <div>
                <h2 class="font-display section-title">Kitchen Insights</h2>
                <p class="insights-subtitle">What your kitchen's been up to</p>
              </div>
              <div class="insights-period-toggle">
                <button v-for="opt in statsPeriodOptions" :key="opt.value" class="period-btn"
                  :class="{ active: statsDays === opt.value }" @click="statsDays = opt.value">
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <div class="insights-grid">
              <!-- Most Cooked Recipes -->
              <div class="ds-card insights-card">
                <div class="insights-card-header">
                  <div class="insights-card-title">Most Cooked Recipes</div>
                  <div class="insights-card-subtitle">Times cooked · {{ statsPeriodLabel }}</div>
                </div>
                <div v-if="loadingRecipes" class="insights-skeleton-list">
                  <div v-for="n in 5" :key="n" class="ds-card skeleton insights-skeleton-row" />
                </div>
                <div v-else-if="topRecipes.length === 0" class="insights-empty">
                  <div class="insights-empty-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"
                        fill="currentColor" />
                    </svg>
                  </div>
                  <div class="insights-empty-title">No cooked meals yet</div>
                  <div class="insights-empty-desc">Insights appear once you've got a little history in this window.
                  </div>
                </div>
                <ol v-else class="insights-list">
                  <li v-for="(recipe, i) in topRecipes" :key="recipe.recipeId" class="insights-row"
                    @click="router.push(`/recipes/${recipe.recipeId}`)">
                    <span class="insights-rank">{{ i + 1 }}</span>
                    <span class="insights-name">{{ recipe.title }}</span>
                    <div class="insights-bar-wrap">
                      <div class="insights-bar insights-bar--green"
                        :style="{ width: Math.round((recipe.count / maxRecipeCount) * 100) + '%' }" />
                    </div>
                    <span class="insights-count">{{ recipe.count }}</span>
                  </li>
                </ol>
              </div>

              <!-- Most Bought Items -->
              <div class="ds-card insights-card">
                <div class="insights-card-header">
                  <div class="insights-card-title">Most Bought Items</div>
                  <div class="insights-card-subtitle">Times added · {{ statsPeriodLabel }}</div>
                </div>
                <div v-if="loadingItems" class="insights-skeleton-list">
                  <div v-for="n in 5" :key="n" class="ds-card skeleton insights-skeleton-row" />
                </div>
                <div v-else-if="topItems.length === 0" class="insights-empty">
                  <div class="insights-empty-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"
                        fill="currentColor" />
                    </svg>
                  </div>
                  <div class="insights-empty-title">Nothing bought yet</div>
                  <div class="insights-empty-desc">Insights appear once you've got a little history in this window.
                  </div>
                </div>
                <ol v-else class="insights-list">
                  <li v-for="(item, i) in topItems" :key="item.name" class="insights-row"
                    :class="{ 'insights-row--clickable': item.ingredientId }"
                    @click="item.ingredientId && router.push(`/ingredients/${item.ingredientId}`)">
                    <span class="insights-rank">{{ i + 1 }}</span>
                    <span class="insights-name">{{ item.name }}</span>
                    <div class="insights-bar-wrap">
                      <div class="insights-bar insights-bar--orange"
                        :style="{ width: Math.round((item.count / maxItemCount) * 100) + '%' }" />
                    </div>
                    <span class="insights-count">{{ item.count }}</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <!-- Right sidebar -->
        <div class="sidebar-column">

          <!-- Active shopping list -->
          <div>
            <div class="section-header-row section-header-row--sm">
              <h2 class="font-display section-title section-title--sm">Active Shopping List</h2>
              <button @click="router.push('/shopping-lists')" class="link-btn link-btn--sm">All lists →</button>
            </div>
            <div v-if="activeList" class="ds-card shopping-active-card">
              <div class="shopping-active-header">
                <div class="font-display shopping-active-title">{{ activeList.title || activeList.name }}</div>
                <span class="ds-badge shopping-active-badge">active</span>
              </div>
              <div class="shopping-progress-track">
                <div class="shopping-progress-fill" :style="{ width: activeListProgress + '%' }" />
              </div>
              <div class="shopping-active-footer">
                <span>{{ activeListProgress }}% completed</span>
                <button @click="router.push(`/shopping-lists/${activeList.id}`)" class="link-btn link-btn--sm">View
                  →</button>
              </div>
            </div>
            <div v-else class="ds-card shopping-empty-card">
              <div class="shopping-empty-text">No active shopping list</div>
              <button @click="router.push('/shopping-lists')" class="ds-btn-secondary shopping-empty-btn">Create
                List</button>
            </div>
          </div>

          <!-- Recent shopping lists -->
          <div v-if="allShoppingLists.length > 0">
            <div class="section-header-row section-header-row--sm">
              <h2 class="font-display section-title section-title--sm">Recent Lists</h2>
              <button @click="router.push('/shopping-lists')" class="link-btn link-btn--sm">All lists →</button>
            </div>
            <div class="recent-lists">
              <div v-for="list in allShoppingLists.slice(0, 3)" :key="list.id" class="ds-card recent-list-item"
                @click="router.push(`/shopping-lists/${list.id}`)">
                <div class="recent-list-info">
                  <div class="recent-list-title">{{ list.title || list.name }}</div>
                  <div class="recent-list-count">{{ list.items_count ?? list.items?.length ?? 0 }} items</div>
                </div>
                <span class="ds-badge" :style="{
                  background: list.status === 'active' ? '#EDF3EC' : list.status === 'completed' ? '#F0FDF4' : '#FFF7ED',
                  color: list.status === 'active' ? '#4F8646' : list.status === 'completed' ? '#16A34A' : '#EA580C'
                }">
                  {{ list.status }}
                </span>
              </div>
            </div>
          </div>

          <!-- Group promo card -->
          <div class="group-promo-card" data-tour="group">
            <div class="group-promo-name">{{ currentGroup?.active_group?.name || 'Personal' }}</div>
            <div class="group-promo-text">Plan meals together. Share recipes with your group.</div>
            <button @click="router.push('/user')" class="group-promo-btn">Manage Group →</button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Page wrapper ── */
.home-page {
  background: var(--bg-app);
  min-height: 100vh;
}

/* ── Hero ── */
.hero-banner {
  background: linear-gradient(135deg, #3F4A3C 0%, #4F8646 100%);
  position: relative;
  overflow: hidden;
}

.hero-texture {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(255, 255, 255, .04) 1px, transparent 1px);
  background-size: 24px 24px;
  pointer-events: none;
}

.hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
}

.hero-date-label {
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, .5);
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.hero-title {
  font-size: 44px;
  font-weight: 700;
  color: white;
  letter-spacing: -1.5px;
  line-height: 1.1;
  margin-bottom: 10px;
}

.hero-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, .65);
  margin-bottom: 32px;
}

.hero-strong {
  color: white;
}

.hero-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.hero-btn {
  background: rgba(255, 255, 255, .15);
  border: 1px solid rgba(255, 255, 255, .25);
  color: white;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: Inter, sans-serif;
  backdrop-filter: blur(4px);
  transition: background .15s;
}

.hero-btn:hover {
  background: rgba(255, 255, 255, .22);
}

@media (max-width: 599px) {
  .hero-title {
    font-size: 32px;
    letter-spacing: -1px;
  }
}

/* ── Stat cards ── */
.stat-card {
  padding: 20px 22px;
}

.stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.stat-icon--green {
  background: #EDF3EC;
}

.stat-icon--orange {
  background: rgba(255, 152, 0, .1);
}

.stat-icon--gray {
  background: #F8FAFC;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #0F172A;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-top: 4px;
}

.stat-sublabel {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 2px;
}

/* ── Section headers ── */
.week-section {
  margin-bottom: 40px;
}

.section-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-header-row--lg {
  margin-bottom: 18px;
}

.section-header-row--sm {
  margin-bottom: 14px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0F172A;
}

.section-title--sm {
  font-size: 16px;
}

.link-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #4F8646;
  font-family: Inter, sans-serif;
  padding: 0;
}

.link-btn--sm {
  font-size: 12px;
}

/* ── Week day cells ── */
.week-day {
  border-radius: 14px;
  padding: 14px 10px;
  text-align: center;
  cursor: pointer;
  user-select: none;
  transition: all .15s;
  background: white;
  border: 1px solid rgba(226, 232, 240, .8);
  box-shadow: 0 1px 3px rgba(0, 0, 0, .04);
}

.week-day.today {
  background: #EDF3EC;
  border-color: #DCE7DA;
  box-shadow: 0 4px 12px rgba(79, 134, 70, .15);
}

.day-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  margin-bottom: 6px;
  color: #94A3B8;
}

.day-label.today {
  color: #4F8646;
}

.day-date {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #0F172A;
}

.day-date.today {
  color: #4F8646;
}

.meal-dots {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.meal-dot {
  height: 4px;
  border-radius: 9999px;
  background: #4F8646;
}

.day-meal-count {
  font-size: 10px;
  color: #94A3B8;
  margin-top: 5px;
}

/* ── Upcoming meals ── */
.meals-loading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meals-skeleton {
  height: 72px;
}

.meals-empty {
  padding: 40px;
  text-align: center;
}

.meals-empty-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.meals-empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 8px;
}

.meals-empty-desc {
  font-size: 13px;
  color: #94A3B8;
  margin-bottom: 20px;
}

.meals-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meals-more {
  text-align: center;
  padding-top: 8px;
}

.meal-item {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: all .15s;
}

.meal-item:hover {
  box-shadow: 0 4px 16px rgba(79, 134, 70, .12);
}

.meal-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #EDF3EC;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.meal-initials {
  font-size: 11px;
  font-weight: 700;
  color: #4F8646;
}

.meal-info {
  flex: 1;
  min-width: 0;
}

.meal-title {
  font-weight: 700;
  font-size: 14px;
  color: #0F172A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meal-date {
  font-size: 12px;
  color: #94A3B8;
  margin-top: 2px;
}

.meal-chevron {
  color: #CBD5E1;
  flex-shrink: 0;
}

/* ── Today's menu ── */
.today-menu {
  margin-top: 24px;
  background: linear-gradient(135deg, #EDF3EC, #f5fbf4);
  border-radius: 16px;
  padding: 20px 22px;
  border: 1px solid #DCE7DA;
}

.today-menu-title {
  font-size: 12px;
  font-weight: 700;
  color: #4F8646;
  letter-spacing: .8px;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.today-menu-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.today-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.today-menu-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4F8646;
  flex-shrink: 0;
}

.today-menu-name {
  font-size: 14px;
  font-weight: 600;
  color: #1A1C1C;
}

.today-menu-badge {
  margin-left: auto;
}

/* ── Sidebar ── */
.sidebar-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.shopping-active-card {
  padding: 20px 22px;
}

.shopping-active-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.shopping-active-title {
  font-weight: 700;
  font-size: 15px;
  color: #0F172A;
}

.shopping-active-badge {
  background: #EDF3EC;
  color: #4F8646;
}

.shopping-progress-track {
  height: 6px;
  background: #F1F5F9;
  border-radius: 9999px;
  margin-bottom: 8px;
}

.shopping-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4F8646, #6AAD5E);
  border-radius: 9999px;
}

.shopping-active-footer {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #94A3B8;
}

.shopping-empty-card {
  padding: 20px;
  text-align: center;
}

.shopping-empty-text {
  font-size: 13px;
  color: #94A3B8;
  margin-bottom: 12px;
}

.shopping-empty-btn {
  font-size: 12px;
  height: 32px;
  padding: 0 14px;
}

.recent-lists {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recent-list-item {
  padding: 14px 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  cursor: pointer;
  transition: all .15s;
}

.recent-list-item:hover {
  background: #FAFAF9;
}

.recent-list-info {
  flex: 1;
  min-width: 0;
}

.recent-list-title {
  font-size: 13px;
  font-weight: 700;
  color: #0F172A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recent-list-count {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 2px;
}

.group-promo-card {
  background: linear-gradient(135deg, #3F4A3C, #4F8646);
  border-radius: 16px;
  padding: 20px 22px;
}

.group-promo-name {
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, .7);
  margin-bottom: 4px;
}

.group-promo-text {
  font-size: 14px;
  font-weight: 700;
  color: white;
  margin-bottom: 10px;
}

.group-promo-btn {
  background: rgba(255, 255, 255, .15);
  border: 1px solid rgba(255, 255, 255, .3);
  color: white;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: Inter, sans-serif;
}

/* ── Kitchen Insights ── */
.insights-section {
  margin-top: 40px;
}

.insights-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.insights-subtitle {
  font-size: 13px;
  color: #64748B;
  margin-top: 2px;
}

.insights-period-toggle {
  display: flex;
  gap: 4px;
  background: #F1F5F9;
  border-radius: 10px;
  padding: 3px;
  flex-shrink: 0;
}

.period-btn {
  background: none;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  cursor: pointer;
  font-family: Inter, sans-serif;
  transition: all .15s;
}

.period-btn.active {
  background: white;
  color: #0F172A;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
}

.period-btn:hover:not(.active) {
  color: #334155;
}

.insights-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}

@media (max-width: 767px) {
  .insights-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.insights-card {
  padding: 20px 22px;
}

.insights-card-header {
  margin-bottom: 16px;
}

.insights-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0F172A;
}

.insights-card-subtitle {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 2px;
}

.insights-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.insights-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #F1F5F9;
}

.insights-row:last-child {
  border-bottom: none;
}

.insights-row--clickable {
  cursor: pointer;
}

.insights-row--clickable:hover .insights-name {
  color: #4F8646;
}

.insights-rank {
  font-size: 11px;
  font-weight: 700;
  color: #CBD5E1;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.insights-name {
  font-size: 13px;
  font-weight: 600;
  color: #0F172A;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color .12s;
}

.insights-bar-wrap {
  width: 80px;
  height: 6px;
  background: #F1F5F9;
  border-radius: 9999px;
  flex-shrink: 0;
  overflow: hidden;
}

.insights-bar {
  height: 100%;
  border-radius: 9999px;
  transition: width .3s ease;
  min-width: 4px;
}

.insights-bar--green {
  background: linear-gradient(90deg, #4F8646, #6AAD5E);
}

.insights-bar--orange {
  background: linear-gradient(90deg, #FF9800, #FFB23E);
}

.insights-count {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  width: 24px;
  text-align: right;
  flex-shrink: 0;
}

.insights-skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.insights-skeleton-row {
  height: 32px;
}

.insights-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px;
  text-align: center;
  gap: 10px;
}

.insights-empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #EEF1F4;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94A3B8;
  flex-shrink: 0;
}

.insights-empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #0F172A;
}

.insights-empty-desc {
  font-size: 12px;
  color: #94A3B8;
  max-width: 220px;
  line-height: 1.5;
}

@media (max-width: 599px) {
  .insights-card {
    padding: 16px;
  }

  .insights-bar-wrap {
    width: 60px;
  }
}
</style>
